"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { Heading, Section } from "../primitives";
import { usePrefersReducedMotion } from "../shared/usePrefersReducedMotion";
import styles from "./FloatingStatement.module.css";
import type { FloatingStatementProps } from "./FloatingStatement.types";

const MAX = 10;

/**
 * Une phrase seule, centrée, qui flotte entre deux sections, avec UN mot qui
 * se remplace toutes les 3 s dans le surligneur de charte (les lettres du
 * nouveau mot reviennent du flou, comme dans le hero du site). Figée sur le
 * premier mot en mouvement réduit.
 *
 * `watermark` pose une image décorative derrière la phrase (19/09). Elle est
 * `aria-hidden` : purement visuelle, jamais porteuse de sens.
 */
export function FloatingStatement({ prefix, lead, words, suffix, interval = 3000, as = "p", size = "h2", tone = "white", watermark, id }: FloatingStatementProps) {
  const list = words.slice(0, MAX);
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced || list.length <= 1) return;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % list.length), interval);
    return () => window.clearInterval(timer);
  }, [reduced, list.length, interval]);

  const word = list[index] ?? "";

  const heading = (
    <Heading as={as} size={size} align="center" className={styles.statement}>
      <span className={styles.prefix}>{prefix}</span>{" "}
      <span className={styles.last}>
        {lead ? <>{lead} </> : null}
        <mark className={styles.word} data-word={word} key={index}>
          {reduced
            ? word
            : Array.from(word).map((ch, i) => (
                <span key={i} className={styles.char} style={{ animationDelay: `${i * 0.03}s` } as CSSProperties}>
                  {ch}
                </span>
              ))}
        </mark>
        {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
      </span>
    </Heading>
  );

  return (
    <Section
      tone={tone}
      id={id}
      className={[styles.section, watermark ? styles.hasWatermark : null].filter(Boolean).join(" ")}
      style={watermark ? ({ "--paf-watermark-offset": `${watermark.offsetY ?? 0}px` } as CSSProperties) : undefined}
    >
      {watermark ? (
        /* Le filigrane s'ancre sur LA PHRASE, pas sur la section : sinon toute
           variation de l'air de la section le décale par rapport au texte. */
        <div className={styles.inner}>
          <span
            aria-hidden
            className={styles.watermark}
            style={
              {
                backgroundImage: `url(${watermark.src})`,
                width: `${watermark.width ?? 900}px`,
                opacity: watermark.opacity ?? 0.05,
              } as CSSProperties
            }
          />
          {heading}
        </div>
      ) : (
        heading
      )}
    </Section>
  );
}
